import React, { useState } from 'react';
import { X, UserPlus2, UserCircle2, ChevronRight } from 'lucide-react';
import { useNewJoiners } from '../../../../hooks/manager_portal_hooks';
import TeamMemberDetailModal from '../../TeamMemberDetailModal';

/**
 * New Joiners Modal
 *
 * Opened from the "My Team – New joiners (last 15 days)" card on the Manager
 * Dashboard.
 * Lists the manager's team members whose EmploymentStartDate on
 * `/data/EmployeesV2` falls within the last 15 days (sourced from
 * `useNewJoiners` — the same query that feeds the card count, so no additional
 * API call is made here).
 *
 * Selecting a member opens the shared TeamMemberDetailModal, keeping the
 * drill-down consistent with the Team Headcount modal and the "My Team" tab.
 */
const statusStyle = (status) =>
  status === 'On Leave'
    ? { bg: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-200' }
    : { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' };

const formatJoinDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const NewJoinersModal = ({ onClose }) => {
  const { newJoiners, isLoading, error } = useNewJoiners();
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="New joiners in my team"
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <UserPlus2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">My Team – New Joiners</h3>
                <p className="text-sm text-gray-500">
                  {isLoading
                    ? 'Loading…'
                    : `${newJoiners.length} joined in the last 15 days`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading new joiners…</p>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-red-500 text-sm">Failed to load new joiners. Please try again.</p>
              </div>
            ) : newJoiners.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500 text-sm">No new joiners in the last 15 days.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {newJoiners.map((member) => {
                  const s = statusStyle(member.status);
                  const joined = formatJoinDate(member.employmentStartDate);
                  return (
                    <li key={member.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedMember(member)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-green-200 hover:bg-green-50/40 transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <UserCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {member.role}
                            {joined ? ` · Joined ${joined}` : ''}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${s.bg} ${s.text} ${s.ring}`}>
                          {member.status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {selectedMember && (
        <TeamMemberDetailModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </>
  );
};

export default NewJoinersModal;
